/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Alert, Box, Grid, Paper, Typography } from '@mui/material';
import { getIntegrationStatus } from 'application/Shared/Components/Cells/IntegrationStatusCell';

/*  Shared  */
import {
    ManagedTableV2UI,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { Notify } from 'application/Shared/Helpers';
import { Confirm } from 'application/Shared/Modals';
import { ClientLink } from 'application/Shared/types/clientLink';

/* internal */
import { usePurgeTenant } from 'hooks/dataAccess/Tenant/tenant';

/* NPM */
import { TFunction } from 'i18next';

/*  React  */
import React, { Dispatch, SetStateAction } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { usePartnerClientsTableContext } from './PartnerClientsTableContext';

interface PurgeClientProps {
    id: string | undefined;
    purgeTenant: (id: string) => Promise<boolean>;
    reloadSessionPartnerTenantLinks: () => void;
    setClientToDelete: Dispatch<SetStateAction<ClientLink | null>>;
    setRerender: ((rerender: boolean) => void) | undefined;
    t: TFunction<'translation', undefined>;
}

const purgeClient = async ({
    id,
    purgeTenant,
    reloadSessionPartnerTenantLinks,
    setClientToDelete,
    setRerender,
    t
}: PurgeClientProps) => {
    let response: boolean = false;
    if (id) {
        response = await purgeTenant(id);

        if (response) {
            Notify.s(t('Partner.dashboard.clientDeleted.success'));

            // If a tenant is removed, reload the partner tenant links
            reloadSessionPartnerTenantLinks();
            if (setRerender) {
                setRerender(true);
            }
        } else {
            Notify.e(t('Partner.dashboard.clientDeleted.failed'));
        }
        setClientToDelete(null);
    }
};

const csvExportFields = (t: TFunction<'translation', undefined>) => [
    { key: 'tenant.name', label: t('Partner.dashboard.tenant.name') },
    { key: 'tenant.id', label: t('Partner.dashboard.tenant.id') },
    {
        key: 'tenant.userCount',
        label: t('Partner.dashboard.activeUserCount')
    },
    {
        key: 'microsoftStatus',
        label: t('Partner.dashboard.microsoft.identityintegrationStatus')
    },
    {
        key: 'googleStatus',
        label: t('Partner.dashboard.google.identityIntegrationStatus')
    },
    {
        key: 'oktaStatus',
        label: t('Partner.dashboard.okta.identityIntegrationStatus')
    },
    { key: 'deviceCount', label: t('Partner.dashboard.devices') },
    {
        key: 'classifiedApps',
        label: t('Partner.dashboard.applicationsClassified')
    },
    {
        key: 'unclassifiedApps',
        label: t('Partner.dashboard.applicationsUnclassified')
    },
    {
        key: 'totalApps',
        label: t('Partner.dashboard.totalApplications')
    }
];

export const PartnerClientsTable = () => {
    const { t } = useTranslation();
    const {
        clientApps,
        clientDevices,
        clientToDelete,
        excludeInternal,
        internalId,
        reloadSessionPartnerTenantLinks,
        setClientToDelete
    } = usePartnerClientsTableContext();
    const { bulkActions, data, setRerender } =
        useManagedTableV2Context<ClientLink>();

    const csvData = data?.map((client: ClientLink) => {
        const clientId = client.id;
        const totalApps =
            clientApps[clientId]?.applicationBreakdown?.total || 0;
        const classifiedApps =
            clientApps[clientId]?.applicationBreakdown?.classified || 0;
        const unclassifiedApps =
            clientApps[clientId]?.applicationBreakdown?.unclassified || 0;
        const deviceCount = clientDevices[client.linkedTenantId] || 0;

        // Get integration status for all providers
        const integrationStatuses = getIntegrationStatus(
            client.provider || [],
            t
        );

        // Extract status for each specific provider type
        const googleStatus = integrationStatuses.find(
            ({ provider }) => provider === 'Google'
        )?.integrationStatus;
        const microsoftStatus = integrationStatuses.find(
            ({ provider }) => provider === 'Microsoft'
        )?.integrationStatus;
        const oktaStatus = integrationStatuses.find(
            ({ provider }) => provider === 'Okta'
        )?.integrationStatus;

        return {
            ...client,
            classifiedApps,
            deviceCount,
            googleStatus,
            microsoftStatus,
            oktaStatus,
            totalApps,
            unclassifiedApps
        };
    });

    const currentCsvExportFields = csvExportFields(t);

    const { purgeTenant } = usePurgeTenant(t);

    const showNotice = !!data?.length && !excludeInternal && !internalId;

    return (
        <Box data-testid="partnerCliensTable">
            <Paper elevation={0} variant="outlined" sx={{ mt: '24px', p: 0 }}>
                {showNotice && (
                    <Alert
                        data-testid="partnerCliensTableShowNotice"
                        severity="warning"
                        style={{ margin: '8px' }}
                        icon={false}
                    >
                        {t('Notice.Clients.no_internal')}
                    </Alert>
                )}
                <ManagedTableV2UI
                    bulkActions={bulkActions}
                    csvData={csvData}
                    csvExport
                    csvExportFields={currentCsvExportFields}
                    csvExportFileName="client_list"
                    renderFooter
                    titleDef={{ titleKey: 'Common.clientsWithCount' }}
                />
            </Paper>
            {clientToDelete && (
                <Confirm
                    modalOpen={!!clientToDelete}
                    promptConfirmFn={() =>
                        purgeClient({
                            id: clientToDelete?.tenant?.id,
                            purgeTenant,
                            reloadSessionPartnerTenantLinks,
                            setClientToDelete,
                            setRerender,
                            t
                        })
                    }
                    promptCancelFn={() => {
                        setClientToDelete(null);
                    }}
                    promptTitle={t('Clients.permanentlyDelete')}
                    promptText={
                        <Grid container spacing={2}>
                            <Grid item xs={1}>
                                <IconWithVariant
                                    data-testid="warningIconDeleteClient"
                                    variant="danger"
                                    src="WarningTriangle"
                                    size="36px"
                                />
                            </Grid>
                            <Grid item xs={11}>
                                <Trans
                                    i18nKey="Clients.permanentlyDeleteWarning"
                                    values={{
                                        clientName: clientToDelete?.tenant?.name
                                    }}
                                    components={{
                                        ClientName: (
                                            <Typography
                                                variant="span"
                                                color="textSecondary"
                                                fontWeight={600}
                                            />
                                        )
                                    }}
                                />
                            </Grid>
                        </Grid>
                    }
                />
            )}
        </Box>
    );
};
